import { Button, Input, SegmentedControl, type SegmentedControlOption, Select, type SelectOption } from "@design";
import { Plus, Search } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Page } from "@/components/Page";
import { type Crumb, PageBreadcrumbs } from "@/components/PageBreadcrumbs";

export interface EntityListPageProps {
  title: string;
  subtitle: string;
  newLabel: string;
  onNew: () => void;
  /** Hide the New action (e.g. the organization turned adding off). Defaults to true. */
  canCreate?: boolean;
  /** Extra actions rendered beside the New button (e.g. an Import button). */
  headerActions?: ReactNode;
  search: string;
  onSearch: (value: string) => void;
  /** Ownership/visibility filter. Omit to hide the control. */
  filter?: { options: SegmentedControlOption[]; value: string; onChange: (value: string) => void; label: string };
  /** Sort order. Omit to hide the control. */
  sort?: { options: SelectOption[]; value: string; onChange: (value: string) => void; label: string };
  /** Pagination footer, e.g. a "Load more" button. */
  footer?: ReactNode;
  /** Breadcrumb ancestors, for a list that sits below something. */
  trail?: Crumb[];
  children: ReactNode;
}

/**
 * The shared Build list page (docs/design/web/WEB_BUILD_PATTERN_PLAN.md §2): PageHeader
 * with a single primary New action, a toolbar (search / filter / sort), and
 * a row list. One instance per entity type, composed from feature code.
 */
export function EntityListPage({
  title,
  subtitle,
  newLabel,
  onNew,
  canCreate = true,
  headerActions,
  search,
  onSearch,
  filter,
  sort,
  footer,
  trail,
  children,
}: Readonly<EntityListPageProps>) {
  const { t } = useTranslation("build");
  return (
    <>
      {trail && <PageBreadcrumbs trail={trail} current={title} />}
      <Page
        title={title}
        subtitle={subtitle}
        actions={
          <>
            {headerActions}
            {canCreate && (
              <Button variant="primary" leadingIcon={<Plus size={16} />} onClick={onNew}>
                {newLabel}
              </Button>
            )}
          </>
        }
      >
        <div className="flex flex-col gap-3 md:flex-row md:items-center">
          <div className="md:max-w-xs md:flex-1">
            <Input
              leadingIcon={<Search />}
              placeholder={t("list.searchPlaceholder")}
              aria-label={t("list.searchPlaceholder")}
              value={search}
              onChange={(e) => onSearch(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-3">
            {filter && (
              <SegmentedControl
                aria-label={filter.label}
                options={filter.options}
                value={filter.value}
                onChange={filter.onChange}
              />
            )}
            {sort && (
              <div className="w-44">
                <Select aria-label={sort.label} options={sort.options} value={sort.value} onChange={sort.onChange} />
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-2">{children}</div>
        {footer}
      </Page>
    </>
  );
}
